// Session state (P2.2) — Architecture §7's middle domain, as pure data and pure functions.
//
// §7 names three state domains and warns that "conflating these is the classic way audio apps rot".
// This file is the second one: "session state (loaded tracks, mixer/FX settings, pad kits, mappings,
// theme, layout): a plain serializable document… Mutations flow through actions".
//
// **What must never live here is the first domain.** Real-time engine state — playhead position,
// live BPM, meter levels, active loops — is "owned by the audio thread. Published outward as
// versioned snapshots via SAB at UI rate. The UI *renders* these; it never computes transport time
// from its own clocks. This single rule eliminates an entire class of drift and desync bugs."
//
// The rule is easy to state and easy to erode: a playhead in the store looks harmless, is
// convenient once, and then the store is being written 375 times a second and every subscriber
// re-renders at audio rate. `tests/unit/session.test.ts` enforces it mechanically by walking this
// state's own key names, because a rule nothing checks is a rule with a countdown on it. The
// complementary direction is already machine-checked: `engines-no-store` forbids /audio and /visual
// from importing this folder at all.
//
// Zustand lives next door in `store.ts`. Everything here is a plain function over plain data, so
// the logic is testable without React, without Zustand, and without a browser — the same split the
// repo uses for /audio/dsp against the worklet shell.

import type { DeckId } from '../platform/types/control';

/**
 * Bumped whenever the shape below changes incompatibly.
 *
 * Nothing persists yet — the IndexedDB/OPFS work is M3's — so this version currently guards
 * nothing. It exists anyway because Architecture §7 requires "a schema version + migration path
 * from day one: v1 users' data must survive every future update (D1: real product)", and a store
 * that ships without a version field does not gain one gracefully. The cost today is one integer.
 */
export const SESSION_SCHEMA_VERSION = 1;

/**
 * What is known about a loaded track *as session data* — the parts that survive a reload and
 * describe the file rather than the playback.
 *
 * Deliberately thin. Title is the filename until M9's library gives tracks real metadata; BPM and
 * key are absent because M2 has no analysis to produce them (they arrive with M3). `frameCount` and
 * `sampleRateHz` are here because they are properties of the decoded file, fixed at load — not live
 * values. `durationSeconds` is derived from them and stored for the same reason: it never changes
 * while the track plays.
 */
export interface LoadedTrack {
  readonly title: string;
  readonly frameCount: number;
  readonly sampleRateHz: number;
  readonly durationSeconds: number;
}

/**
 * One deck's session state.
 *
 * `playing` is **intent**, not observation: it records that the user asked for playback, which is a
 * serializable fact about the session. Whether audio is actually flowing this instant belongs to
 * the audio thread and is read from SAB. Keeping the distinction is what lets this field live here
 * at all — the moment it tried to mean "audio is currently audible" it would be real-time state
 * wearing a session-state name.
 */
export interface DeckSession {
  readonly track: LoadedTrack | null;
  /**
   * FR-02's cue point, in frames. `null` when unset.
   *
   * Session state because it changes only when a human sets it, and because it is exactly the kind
   * of thing FR-03's hot cues will persist alongside at M3.
   */
  readonly cuePoint: number | null;
  readonly playing: boolean;
}

/**
 * The whole session document.
 *
 * Both decks exist from P2.2 even though M2 only drives deck A — `DeckId` is `'a' | 'b'`, so a
 * `Record` over it is exhaustive by construction and *simpler* than special-casing one deck now and
 * generalising later. M3 fills the empty slot rather than reshaping the document.
 */
export interface SessionState {
  readonly schemaVersion: number;
  readonly decks: Record<DeckId, DeckSession>;
}

export function emptyDeck(): DeckSession {
  return { track: null, cuePoint: null, playing: false };
}

export function initialSession(): SessionState {
  return {
    schemaVersion: SESSION_SCHEMA_VERSION,
    decks: { a: emptyDeck(), b: emptyDeck() },
  };
}

// ── Transitions ──────────────────────────────────────────────────────────────────────────────
//
// Pure `(state, args) => state`. Zustand's `set` applies them in store.ts; tests call them
// directly. Returning a new object rather than mutating is what makes Zustand's selector
// subscriptions work at all — a mutated object is reference-equal to itself and no subscriber
// learns anything.

function withDeck(
  state: SessionState,
  deckId: DeckId,
  change: (deck: DeckSession) => DeckSession,
): SessionState {
  return {
    ...state,
    decks: { ...state.decks, [deckId]: change(state.decks[deckId]) },
  };
}

/** A new track replaces everything about the deck — a stale cue point from the previous track
 * would point into audio that is no longer loaded, which is worse than having no cue at all. */
export function loadTrack(
  state: SessionState,
  deckId: DeckId,
  track: LoadedTrack,
): SessionState {
  return withDeck(state, deckId, () => ({ track, cuePoint: null, playing: false }));
}

export function unloadTrack(state: SessionState, deckId: DeckId): SessionState {
  return withDeck(state, deckId, () => emptyDeck());
}

/** Clamped to the loaded track, because a cue point outside it is not a cue point. Setting one
 * with no track loaded is a no-op rather than an error: it is a UI race, not a bug worth throwing
 * over. */
export function setCuePoint(
  state: SessionState,
  deckId: DeckId,
  frame: number,
): SessionState {
  return withDeck(state, deckId, (deck) => {
    if (deck.track === null) {
      return deck;
    }
    const lastFrame = Math.max(0, deck.track.frameCount - 1);
    return { ...deck, cuePoint: Math.min(lastFrame, Math.max(0, Math.round(frame))) };
  });
}

export function clearCuePoint(state: SessionState, deckId: DeckId): SessionState {
  return withDeck(state, deckId, (deck) => ({ ...deck, cuePoint: null }));
}

/** Transport intent. A deck with no track cannot be playing — guarding here rather than at every
 * call site keeps the invariant true by construction. */
export function setPlaying(
  state: SessionState,
  deckId: DeckId,
  playing: boolean,
): SessionState {
  return withDeck(state, deckId, (deck) =>
    deck.track === null ? deck : { ...deck, playing },
  );
}

// ── Migration ────────────────────────────────────────────────────────────────────────────────

/**
 * Turns whatever was persisted into state this build can use.
 *
 * Nothing calls this yet — persistence is M3 — and it is written now because a migration path
 * retrofitted after the first release is a migration path with a gap in it. Today's only real case
 * is the honest one: anything this build does not recognise (a newer schema, a corrupted blob, a
 * shape from a version that predates the field) is discarded for a clean session rather than
 * half-read into an inconsistent one.
 *
 * Losing a session is a bad afternoon; a silently malformed session is a bug report six months
 * later with no way to reproduce it.
 */
export function migrateSession(persisted: unknown): SessionState {
  if (typeof persisted !== 'object' || persisted === null) {
    return initialSession();
  }

  const version = (persisted as { schemaVersion?: unknown }).schemaVersion;
  if (version !== SESSION_SCHEMA_VERSION) {
    // Includes the future case — a state written by a *newer* build than this one. Reading it
    // optimistically would be guessing at fields that did not exist when this code was written.
    return initialSession();
  }

  const decks = (persisted as { decks?: unknown }).decks;
  if (typeof decks !== 'object' || decks === null) {
    return initialSession();
  }

  return {
    schemaVersion: SESSION_SCHEMA_VERSION,
    decks: {
      a: migrateDeck((decks as Record<string, unknown>)['a']),
      b: migrateDeck((decks as Record<string, unknown>)['b']),
    },
  };
}

function migrateDeck(value: unknown): DeckSession {
  if (typeof value !== 'object' || value === null) {
    return emptyDeck();
  }
  const deck = value as Partial<DeckSession>;
  const track = deck.track;

  // A deck's track is only usable if every field it needs survived. A partially-read track would
  // produce a UI showing a duration for audio nobody can play.
  const trackIsUsable =
    typeof track === 'object' &&
    track !== null &&
    typeof track.title === 'string' &&
    typeof track.frameCount === 'number' &&
    typeof track.sampleRateHz === 'number' &&
    typeof track.durationSeconds === 'number';

  return {
    track: trackIsUsable ? track : null,
    cuePoint: typeof deck.cuePoint === 'number' ? deck.cuePoint : null,
    // Never restore a session mid-play. A page that starts making noise on load, before the user
    // has touched anything, is the wrong first impression — and browsers forbid it anyway until a
    // gesture (P0.5).
    playing: false,
  };
}
